import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';

import { CapabilitiesService } from './capabilities.service';

/** Route of "Función no disponible" inside the shell (ADR 0031 section 3.3). */
export const FEATURE_UNAVAILABLE_PATH = 'no-disponible';

export interface RequireCapabilityOptions {
  /**
   * The route only starts new work (an inbound command), so a PAUSED capability blocks it too.
   * By default a PAUSED feature stays readable and its screen disables its own actions
   * (ADR 0031 section 3.4).
   */
  blockPaused?: boolean;
}

/**
 * A direct URL of a capability that is not available goes to "Función no disponible", naming the
 * capability, its state and the URL it came from. Waits for this session's first answer, so a
 * reload on the screen never bounces it before CS answered. Roles are checked by `roleGuard`.
 */
export function requireCapability(
  key: string,
  options: RequireCapabilityOptions = {},
): CanActivateFn {
  return async (_route, state) => {
    const capabilities = inject(CapabilitiesService);
    const router = inject(Router);
    await capabilities.whenSettled();
    const current = capabilities.state(key);
    if (current === 'ENABLED' || (current === 'PAUSED' && !options.blockPaused)) return true;
    return router.createUrlTree([`/${FEATURE_UNAVAILABLE_PATH}`], {
      queryParams: { funcion: key, estado: current, desde: state.url },
    });
  };
}
